import { useCallback, useEffect, useState } from "react";

const KEY = "cd_wallboard_config_v2";

const PANEL_DEFAULTS = {
  goalEnabled: true,
  dailyGoal: 200,
  refreshSec: 30,
  showClock: true,
  showKpis: true,
  showStageTiles: true,
  showPipeline: true,
  showLeaderboard: true,
  showTicker: true,
  showFulfillmentTile: true,
};

const SEED_PRESETS = {
  Default: { ...PANEL_DEFAULTS },
  "QA station": {
    ...PANEL_DEFAULTS,
    goalEnabled: false,
    showKpis: false,
    showPipeline: false,
    showLeaderboard: false,
    showTicker: true,
    refreshSec: 10,
  },
  "Loading dock": {
    ...PANEL_DEFAULTS,
    dailyGoal: 50,
    showLeaderboard: false,
    showTicker: true,
    showStageTiles: true,
  },
  Office: { ...PANEL_DEFAULTS },
};

const SEED_STATE = {
  activePreset: "Default",
  presets: SEED_PRESETS,
};

function read() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return SEED_STATE;
    const parsed = JSON.parse(raw);
    if (!parsed?.presets || !parsed?.activePreset) return SEED_STATE;
    // Backfill any missing panel keys (so new fields added later don't break older saves)
    const presets = {};
    for (const [name, c] of Object.entries(parsed.presets)) {
      presets[name] = { ...PANEL_DEFAULTS, ...c };
    }
    if (!presets[parsed.activePreset]) parsed.activePreset = Object.keys(presets)[0] || "Default";
    return { activePreset: parsed.activePreset, presets };
  } catch {
    return SEED_STATE;
  }
}

export default function useWallboardConfig() {
  const [state, setState] = useState(() => read());

  useEffect(() => {
    try { localStorage.setItem(KEY, JSON.stringify(state)); }
    catch (err) { console.warn("Wallboard config persist failed:", err); }
  }, [state]);

  const config = state.presets[state.activePreset] || PANEL_DEFAULTS;

  const update = useCallback((patch) => {
    setState((s) => ({
      ...s,
      presets: { ...s.presets, [s.activePreset]: { ...s.presets[s.activePreset], ...patch } },
    }));
  }, []);

  const selectPreset = useCallback((name) => {
    setState((s) => (s.presets[name] ? { ...s, activePreset: name } : s));
  }, []);

  const createPreset = useCallback((name) => {
    const trimmed = (name || "").trim();
    if (!trimmed) return false;
    setState((s) => {
      if (s.presets[trimmed]) return s; // no-op on duplicate
      const base = s.presets[s.activePreset] || PANEL_DEFAULTS;
      return {
        activePreset: trimmed,
        presets: { ...s.presets, [trimmed]: { ...base } },
      };
    });
    return true;
  }, []);

  const renamePreset = useCallback((oldName, newName) => {
    const trimmed = (newName || "").trim();
    if (!trimmed || trimmed === oldName) return false;
    setState((s) => {
      if (!s.presets[oldName] || s.presets[trimmed]) return s;
      const { [oldName]: cfg, ...rest } = s.presets;
      return {
        activePreset: s.activePreset === oldName ? trimmed : s.activePreset,
        presets: { ...rest, [trimmed]: cfg },
      };
    });
    return true;
  }, []);

  const deletePreset = useCallback((name) => {
    setState((s) => {
      if (!s.presets[name]) return s;
      const { [name]: _removed, ...rest } = s.presets;
      const names = Object.keys(rest);
      if (!names.length) return s; // keep at least one
      return {
        activePreset: s.activePreset === name ? names[0] : s.activePreset,
        presets: rest,
      };
    });
  }, []);

  const resetActive = useCallback(() => {
    setState((s) => ({
      ...s,
      presets: { ...s.presets, [s.activePreset]: { ...PANEL_DEFAULTS } },
    }));
  }, []);

  return {
    config,
    update,
    activePreset: state.activePreset,
    presetNames: Object.keys(state.presets),
    selectPreset,
    createPreset,
    renamePreset,
    deletePreset,
    resetActive,
  };
}
